import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App.tsx';
import './index.css';
import { CodeEditorProps } from './types';

class FormbirdCodeEditor extends HTMLElement {
  private props: CodeEditorProps = {
    initialCode: `function Component() {
  return (
    <div className="p-8 text-center">
      <h1 className="text-3xl font-bold text-blue-600 mb-4">
        Hello, React!
      </h1>
      <p className="text-gray-600">
        Welcome to the React Code Editor
      </p>
    </div>
  );
}`,
    codeType: 'react',
    className: '',
    theme: 'light',
    showFilesExplorer: true,
    showPreview: true,
    showTemplates: true,
    height: '100vh',
    width: '100%'
  };

  private root: ReturnType<typeof createRoot> | null = null;

  static get observedAttributes() {
    return ['initial-code', 'code-type', 'class-name', 'theme', 'show-files-explorer', 'show-preview', 'show-templates', 'height', 'width'];
  }

  attributeChangedCallback(name: string, _oldValue: string, newValue: string) {
    const propName = name.split('-').map((part, index) => 
      index === 0 ? part : part.charAt(0).toUpperCase() + part.slice(1)
    ).join('');

    try {
      if (propName === 'showFilesExplorer' || propName === 'showPreview' || propName === 'showTemplates') {
        this.updateProp(propName as keyof CodeEditorProps, newValue === 'true');
      } else if (propName === 'initialCode' || propName === 'className' || propName === 'theme' || propName === 'height' || propName === 'width') {
        this.updateProp(propName as keyof CodeEditorProps, newValue);
      } else if (propName === 'codeType') {
        this.updateProp(propName as keyof CodeEditorProps, newValue as 'react' | 'nodejs');
      }
    } catch (e) {
      console.error(`Error parsing attribute ${name}:`, e);
    }
    
    this.render();
  }

  private updateProp<K extends keyof CodeEditorProps>(key: K, value: CodeEditorProps[K]) {
    this.props[key] = value;
  }

  connectedCallback() {
    this.render();
  }

  disconnectedCallback() {
    if (this.root) {
      this.root.unmount();
      this.root = null;
    }
  }

  private render() {
    // Clear existing content
    this.innerHTML = '';
    
    // Create mount point
    const mountPoint = document.createElement('div');
    mountPoint.style.height = this.props.height;
    mountPoint.style.width = this.props.width;
    if (this.props.className) {
      mountPoint.className = this.props.className;
    }
    
    this.appendChild(mountPoint);

    // Create React root and render
    this.root = createRoot(mountPoint);
    this.root.render(
      <StrictMode>
        <App {...this.props} />
      </StrictMode>
    );
  }
}

// Define the custom element
customElements.define('formbird-code-editor', FormbirdCodeEditor);
